import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { ImagePlus, Mic, Paperclip, Pin, Plus, Volume2 } from 'lucide-react';
import { Button, EmptyState, ErrorState, Skeleton, toast } from '@helios/ui';
import { hasPermission, PERMISSIONS } from '@helios/authz';
import { ConstructionOsLogo } from '@/components/brand/ConstructionOsLogo';
import { useAuth } from '@/features/auth/authContext';
import { useAiAgentsMutations } from '@/features/ai-agents/aiAgents.hooks';
import { aiChatApi, putToPresignedUrl, sha256Hex } from '@/features/ai-chat/aiChat.api';
import { insuranceChatContext } from '@/features/ai-chat/aiChat.context';
import {
  useAiChatApprovals,
  useAiChatBootstrap,
  useAiChatMessages,
  useAiChatMutations,
  useAiChatThreads,
} from '@/features/ai-chat/aiChat.hooks';
import { useDeviceNarration, useDeviceVoice } from '@/features/ai-chat/useDeviceVoice';
import { hasMessageAudio, useMessageSpeech } from '@/features/ai-chat/useMessageSpeech';
import { useTargetedChatApproval } from '@/features/ai-chat/useTargetedChatApproval';
import { voiceRecordingStillCurrent } from '@/features/ai-chat/voiceRecordingScope';
import { useAiAssistantPreferences } from '@/features/settings/aiSettings.hooks';
import { useUiStore } from '@/stores/uiStore';
import {
  HeliosChatWindow,
  HeliosChatComposerInput,
  HeliosMessageBubble,
  HeliosSetupChatPanel,
  type HeliosSetupChatContext,
} from './HeliosChatWindow';

const SAFE_VOICE_DEFAULTS = {
  voiceOutputEnabled: false,
  assistantVoice: '',
  speechRate: 1,
  voicePitch: 1,
  voiceVolume: 1,
  voiceLanguage: 'en-US',
  continuousListening: false,
};

function recordingExtension(mimeType: string): string {
  const format = mimeType.split(';')[0]?.trim().toLowerCase();
  const extensions: Record<string, string> = {
    'audio/webm': 'webm',
    'audio/ogg': 'ogg',
    'audio/mp4': 'mp4',
    'audio/mpeg': 'mp3',
    'audio/wav': 'wav',
    'audio/x-wav': 'wav',
    'audio/flac': 'flac',
  };
  const extension = format ? extensions[format] : undefined;
  if (!extension)
    throw new Error('This recording format is unsupported. Use a supported audio browser.');
  return extension;
}

function ComposerIconButton({
  label,
  title,
  active,
  disabled,
  onClick,
  children,
}: {
  label: string;
  title?: string;
  active?: boolean;
  disabled?: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  const tip = title ?? label;
  return (
    <span title={tip} className="inline-flex">
      <button
        type="button"
        aria-label={label}
        title={tip}
        disabled={disabled}
        onClick={onClick}
        className={[
          'text-muted-foreground inline-flex h-7 w-7 items-center justify-center rounded-md transition-colors',
          'hover:bg-muted/60 hover:text-foreground hover:shadow-glow',
          'disabled:opacity-40',
          'focus-visible:ring-ring focus-visible:outline-none focus-visible:ring-1',
          active ? 'bg-primary/15 text-primary shadow-glow' : '',
        ].join(' ')}
      >
        {children}
      </button>
    </span>
  );
}

export function HeliosChatPanel({
  setup,
  onReturnFocus,
}: {
  setup?: HeliosSetupChatContext;
  onReturnFocus?: () => void;
} = {}) {
  return setup ? (
    <HeliosSetupChatPanel key={setup.scopeKey} setup={setup} />
  ) : (
    <PlatformHeliosChatPanel onReturnFocus={onReturnFocus} />
  );
}

function PlatformHeliosChatPanel({ onReturnFocus }: { onReturnFocus?: () => void }) {
  const { pathname } = useLocation();
  const requestedContext = insuranceChatContext(pathname);
  const navigate = useNavigate();
  const open = useUiStore((state) => state.advisorOpen);
  const setOpen = useUiStore((state) => state.setAdvisorOpen);
  const { permissions, user, tenant } = useAuth();
  const actorKey = `${tenant?.id ?? user?.tenantId ?? 'none'}:${user?.id ?? 'none'}`;
  const canRead = hasPermission(permissions, PERMISSIONS.AI_CHAT_READ);
  const canWrite = hasPermission(permissions, PERMISSIONS.AI_CHAT_WRITE);
  const canApproveRec = hasPermission(permissions, PERMISSIONS.AI_RECOMMENDATIONS_APPROVE);

  const bootstrap = useAiChatBootstrap(open && canRead);
  const threads = useAiChatThreads(open && canRead);
  const approvals = useAiChatApprovals(open && canRead);
  const mutations = useAiChatMutations();
  const agentMutations = useAiAgentsMutations(null);
  const preferencesQuery = useAiAssistantPreferences();
  const preferences = preferencesQuery.data?.preferences;
  const capabilities = bootstrap.data?.capabilities;
  const allowActionApproval = preferences?.actionMode === 'approval';

  const [activeThreadId, setActiveThreadId] = useState<string | null>(null);
  const approvalReview = useTargetedChatApproval({
    open,
    canRead,
    actorKey,
    activeThreadId,
    setActiveThreadId,
    approvals: approvals.data,
  });
  const [draft, setDraft] = useState('');
  const [pendingAttachmentIds, setPendingAttachmentIds] = useState<string[]>([]);
  const [imagePromptOpen, setImagePromptOpen] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [voicePhase, setVoicePhase] = useState<'idle' | 'starting' | 'recording' | 'processing'>(
    'idle',
  );
  const fileInputRef = useRef<HTMLInputElement>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const scrollerRef = useRef<HTMLDivElement>(null);
  const seenReplies = useRef(new Map<string, string | null>());
  const voiceCapture = useRef<{ recorder: MediaRecorder; stream: MediaStream } | null>(null);
  const voiceScope = useRef({
    actorKey,
    requestedContext,
    open,
    canWrite,
    activeThreadId,
    draft,
    pendingAttachmentIds,
    preferences,
  });
  voiceScope.current = {
    actorKey,
    requestedContext,
    open,
    canWrite,
    activeThreadId,
    draft,
    pendingAttachmentIds,
    preferences,
  };

  const messages = useAiChatMessages(activeThreadId, open && canRead);
  const pendingCount = bootstrap.data?.pendingApprovals ?? 0;
  const deviceVoice = useDeviceVoice({
    preferences: preferences ?? SAFE_VOICE_DEFAULTS,
    enabled: open && canRead && Boolean(preferences),
    scopeKey: `${actorKey}:${activeThreadId ?? 'none'}`,
    onFinalTranscript: (text) => {
      if (!canWrite) return;
      // Preserve an existing typed draft or queued attachment for human review.
      if (
        preferences?.voiceAutoSend &&
        !draft.trim() &&
        pendingAttachmentIds.length === 0 &&
        !mutations.sendMessage.isPending
      ) {
        void handleSend(text);
      } else {
        setDraft((current) => [current.trim(), text].filter(Boolean).join(' '));
      }
    },
  });
  const speakDevice = deviceVoice.speak;
  const stopDeviceListening = deviceVoice.stopListening;
  const stopDeviceSpeaking = deviceVoice.stopSpeaking;
  const { speak: speakReply, stopSpeaking } = useMessageSpeech({
    enabled: open && canRead && Boolean(preferences),
    scopeKey: `${actorKey}:${activeThreadId ?? 'none'}`,
    providerConfigured: Boolean(capabilities?.voiceSpeak),
    canGenerate: canWrite,
    voiceOutputEnabled: Boolean(preferences?.voiceOutputEnabled),
    volume: preferences?.voiceVolume ?? 1,
    speakDevice,
    pauseDeviceListening: deviceVoice.pauseListeningForPlayback,
    stopDeviceSpeaking,
    refreshSavedAudio: async (messageId, attachmentId) => {
      if (!activeThreadId) return undefined;
      const result = await aiChatApi.listMessages(activeThreadId, { limit: 80 });
      if (!result.ok || !result.data) return undefined;
      return (
        result.data.items
          .find((item) => item.id === messageId)
          ?.attachments.find(
            (item) =>
              item.mimeType.startsWith('audio/') &&
              item.uploadComplete &&
              (!attachmentId || item.id === attachmentId),
          )?.downloadUrl ?? undefined
      );
    },
  });
  useEffect(() => {
    seenReplies.current.clear();
    stopDeviceListening();
    stopDeviceSpeaking();
    return () => {
      const capture = voiceCapture.current;
      voiceCapture.current = null;
      if (capture) {
        if (capture.recorder.state !== 'inactive') capture.recorder.stop();
        capture.stream.getTracks().forEach((track) => track.stop());
      }
    };
  }, [actorKey, stopDeviceListening, stopDeviceSpeaking]);
  useEffect(() => {
    if (!canWrite) stopDeviceListening();
    if (open && canWrite) return;
    const capture = voiceCapture.current;
    voiceCapture.current = null;
    if (capture) {
      if (capture.recorder.state !== 'inactive') capture.recorder.stop();
      capture.stream.getTracks().forEach((track) => track.stop());
    }
  }, [open, canWrite, stopDeviceListening]);
  useEffect(
    () => () => {
      const capture = voiceCapture.current;
      voiceCapture.current = null;
      if (capture) {
        if (capture.recorder.state !== 'inactive') capture.recorder.stop();
        capture.stream.getTracks().forEach((track) => track.stop());
      }
    },
    [activeThreadId],
  );
  const pageLabel =
    pathname
      .split('/')
      .filter((part) => part && !/^[0-9a-f-]{12,}$/i.test(part))
      .map((part) => part.replace(/[-_]/g, ' '))
      .join(', ') || 'Home';
  useDeviceNarration({
    enabled:
      open &&
      canRead &&
      Boolean(preferences?.voiceOutputEnabled && preferences.proactiveNarration) &&
      voicePhase === 'idle' &&
      !deviceVoice.listening,
    context: pageLabel,
    speak: speakDevice,
  });

  useEffect(() => {
    if (!open || !activeThreadId || !messages.data) return;
    const lastReply = [...messages.data.items].reverse().find((item) => item.role === 'ASSISTANT');
    const previous = seenReplies.current.get(activeThreadId);
    const hasBaseline = seenReplies.current.has(activeThreadId);
    seenReplies.current.set(activeThreadId, lastReply?.id ?? null);
    // Opening an existing thread must not replay its history.
    if (hasBaseline && lastReply && lastReply.id !== previous) {
      void speakReply(lastReply).catch((error: unknown) => {
        toast.error(error instanceof Error ? error.message : 'Unable to play speech');
      });
    }
  }, [activeThreadId, messages.data, open, speakReply]);

  useEffect(() => {
    if (!open || !threads.data?.length) return;
    if (approvalReview.targetThreadId && approvalReview.targetThreadId === activeThreadId) return;
    if (!activeThreadId || !threads.data.some((thread) => thread.id === activeThreadId)) {
      setActiveThreadId(threads.data[0]!.id);
    }
  }, [open, threads.data, activeThreadId, approvalReview.targetThreadId]);

  useEffect(() => {
    if (!scrollerRef.current) return;
    scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
  }, [messages.data?.items.length, activeThreadId]);

  const ensureThread = async (): Promise<string> => {
    if (activeThreadId) return activeThreadId;
    const thread = await mutations.createThread.mutateAsync(undefined);
    setActiveThreadId(thread.id);
    return thread.id;
  };

  async function handleSend(voiceContent?: string) {
    const content = (voiceContent ?? draft).trim();
    const attachmentIds = voiceContent === undefined ? pendingAttachmentIds : [];
    if (!content && attachmentIds.length === 0) return;
    if (!canWrite) {
      toast.error('You do not have permission to send chat messages');
      return;
    }
    try {
      const threadId = await ensureThread();
      if (!seenReplies.current.has(threadId)) seenReplies.current.set(threadId, null);
      const result = await mutations.sendMessage.mutateAsync({
        threadId,
        content: content || 'Shared an attachment.',
        attachmentIds,
        inputModality: voiceContent === undefined ? 'TEXT' : 'VOICE',
        speakReply: false,
        ...(requestedContext ? { context: requestedContext } : {}),
      });
      if (voiceContent === undefined) {
        setDraft('');
        setPendingAttachmentIds([]);
      }
      if (result.degradedReason) {
        toast.message('Helios replied with platform facts only', {
          description: result.degradedReason,
        });
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Unable to send message');
    }
  }

  const handleAttach = async (file: File) => {
    if (!canWrite) return;
    try {
      const threadId = await ensureThread();
      const sha256 = await sha256Hex(file);
      const kind = file.type.startsWith('image/')
        ? 'IMAGE'
        : file.type.startsWith('audio/')
          ? 'AUDIO'
          : 'FILE';
      const ticket = await aiChatApi.presignAttachment({
        threadId,
        fileName: file.name,
        mimeType: file.type || 'application/octet-stream',
        sizeBytes: file.size,
        sha256,
        kind,
      });
      if (!ticket.ok || !ticket.data) throw new Error(ticket.error ?? 'Unable to start upload');
      await putToPresignedUrl(ticket.data.upload.url, ticket.data.upload.requiredHeaders, file);
      const completed = await aiChatApi.completeAttachment(ticket.data.attachment.id, { sha256 });
      if (!completed.ok || !completed.data) {
        throw new Error(completed.error ?? 'Unable to finalize upload');
      }
      setPendingAttachmentIds((current) => [...current, completed.data!.id]);
      toast.success(`Attached ${file.name}`);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Attachment upload failed');
    }
  };

  const handleVoice = async () => {
    if (!canWrite) return;
    if (voiceCapture.current?.recorder.state === 'recording') {
      voiceCapture.current.recorder.stop();
      return;
    }
    if (voicePhase !== 'idle') return;
    if (deviceVoice.listening) {
      deviceVoice.stopListening();
      return;
    }
    if (!capabilities?.voiceTranscribe && deviceVoice.recognitionSupported) {
      stopSpeaking();
      deviceVoice.startListening();
      return;
    }
    if (!capabilities?.voiceTranscribe) {
      toast.error('OpenAI voice transcription is not configured');
      return;
    }
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
      toast.error('Microphone recording is not available in this browser');
      return;
    }
    const recordingActor = actorKey;
    const recordingThread = activeThreadId;
    const recordingContext = requestedContext;
    let createdThread: string | null = null;
    const stillCurrent = () =>
      voiceScope.current.requestedContext === recordingContext &&
      voiceRecordingStillCurrent({
        open: voiceScope.current.open,
        canWrite: voiceScope.current.canWrite,
        actorKey: voiceScope.current.actorKey,
        recordingActor,
        activeThreadId: voiceScope.current.activeThreadId,
        recordingThread,
        createdThread,
      });
    let capturedStream: MediaStream | undefined;
    let stopTimer: ReturnType<typeof setTimeout> | undefined;
    setVoicePhase('starting');
    stopSpeaking();
    stopDeviceListening();
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      capturedStream = stream;
      if (!stillCurrent()) return;
      const recorder = new MediaRecorder(stream);
      voiceCapture.current = { recorder, stream };
      const chunks: Blob[] = [];
      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) chunks.push(event.data);
      };
      const stopped = new Promise<Blob>((resolve, reject) => {
        recorder.onstop = () =>
          resolve(
            new Blob(chunks, {
              type: recorder.mimeType || chunks[0]?.type || 'audio/webm',
            }),
          );
        recorder.onerror = () => reject(new Error('Voice recording failed'));
      });
      recorder.start();
      setVoicePhase('recording');
      stopTimer = setTimeout(() => {
        if (recorder.state !== 'inactive') recorder.stop();
      }, 8000);
      const blob = await stopped;
      clearTimeout(stopTimer);
      stream.getTracks().forEach((track) => track.stop());
      if (!stillCurrent()) return;
      if (!blob.size) throw new Error('No audio was recorded. Try the microphone again.');
      setVoicePhase('processing');
      const extension = recordingExtension(blob.type);
      const file = new File([blob], `voice-${Date.now()}.${extension}`, { type: blob.type });
      let threadId = recordingThread;
      if (!threadId) {
        const thread = await mutations.createThread.mutateAsync(undefined);
        createdThread = thread.id;
        if (!stillCurrent()) return;
        threadId = thread.id;
        setActiveThreadId(thread.id);
      }
      if (!stillCurrent()) return;
      if (!seenReplies.current.has(threadId)) seenReplies.current.set(threadId, null);
      const sha256 = await sha256Hex(file);
      if (!stillCurrent()) return;
      const ticket = await aiChatApi.presignAttachment({
        threadId,
        fileName: file.name,
        mimeType: file.type,
        sizeBytes: file.size,
        sha256,
        kind: 'AUDIO',
      });
      if (!stillCurrent()) return;
      if (!ticket.ok || !ticket.data)
        throw new Error(ticket.error ?? 'Unable to start voice upload');
      await putToPresignedUrl(ticket.data.upload.url, ticket.data.upload.requiredHeaders, file);
      if (!stillCurrent()) return;
      const completed = await aiChatApi.completeAttachment(ticket.data.attachment.id, { sha256 });
      if (!completed.ok || !completed.data) {
        throw new Error(completed.error ?? 'Unable to finalize voice upload');
      }
      if (!stillCurrent()) return;
      const current = voiceScope.current;
      const autoSend = Boolean(
        current.preferences?.voiceAutoSend &&
          !current.draft.trim() &&
          current.pendingAttachmentIds.length === 0,
      );
      const generateSpeech = Boolean(
        autoSend && current.preferences?.voiceOutputEnabled && capabilities?.voiceSpeak,
      );
      const result = await aiChatApi.transcribeAndSend({
        threadId,
        attachmentId: ticket.data.attachment.id,
        sendMessage: autoSend,
        speakReply: generateSpeech,
        ...(recordingContext ? { context: recordingContext } : {}),
      });
      if (!result.ok || !result.data) throw new Error(result.error ?? 'Voice send failed');
      if (!stillCurrent()) return;
      const voiceResult = result.data;
      if ('kind' in voiceResult && voiceResult.kind === 'transcript') {
        setDraft((value) =>
          [value.trim(), voiceResult.transcript.trim()].filter(Boolean).join(' '),
        );
        setPendingAttachmentIds((value) => [...new Set([...value, voiceResult.attachmentId])]);
        toast.success('OpenAI transcript ready to review');
        return;
      }
      if (!('assistantMessage' in voiceResult)) return;
      if (generateSpeech) {
        // The server already attempted TTS. Play its saved audio without making a second call.
        seenReplies.current.set(threadId, voiceResult.assistantMessage.id);
        await speakReply(voiceResult.assistantMessage, { persistedOnly: true }).catch(
          (error: unknown) => {
            toast.error(error instanceof Error ? error.message : 'Unable to play speech');
          },
        );
      }
      await mutations.invalidateChat(threadId);
      toast.success('Voice message sent');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Voice capture failed');
    } finally {
      clearTimeout(stopTimer);
      capturedStream?.getTracks().forEach((track) => track.stop());
      if (voiceCapture.current?.stream === capturedStream) voiceCapture.current = null;
      setVoicePhase('idle');
    }
  };

  const canAttach = canWrite;
  const canDictate = !capabilities?.voiceTranscribe && Boolean(deviceVoice.recognitionSupported);
  const canRecordVoice =
    Boolean(capabilities?.voiceTranscribe) &&
    Boolean(navigator.mediaDevices?.getUserMedia) &&
    typeof MediaRecorder !== 'undefined';
  const voiceReady = Boolean(preferences) && (canDictate || canRecordVoice);
  const voiceDisabledReason = !canWrite
    ? 'You do not have permission to send voice input'
    : !voiceReady
      ? capabilities?.voiceTranscribe
        ? 'OpenAI transcription is configured; recording needs microphone access and a supported browser'
        : 'OpenAI transcription is not configured and browser speech recognition is unavailable'
      : undefined;
  const canGenerateImage = Boolean(capabilities?.imageGenerate);
  const imageDisabledReason = !canWrite
    ? 'You do not have permission to attach or generate images'
    : undefined;
  const lastAssistant = [...(messages.data?.items ?? [])]
    .reverse()
    .find((item) => item.role === 'ASSISTANT');
  const canSpeakLocally = Boolean(deviceVoice.speechSupported);
  const canSpeakRemote = Boolean(capabilities?.voiceSpeak);
  const hasSavedSpeech = Boolean(lastAssistant && hasMessageAudio(lastAssistant));
  const speakReady =
    Boolean(preferences && lastAssistant) &&
    (hasSavedSpeech || (canSpeakRemote ? canWrite : canSpeakLocally));
  const speakDisabledReason = !lastAssistant
    ? 'No assistant reply to read aloud'
    : !speakReady
      ? canSpeakRemote
        ? 'Generating OpenAI speech requires permission to write chat messages'
        : 'OpenAI speech is not configured and browser speech playback is unavailable'
      : undefined;
  const acceptDroppedFiles = (fileList: FileList | File[]) => {
    const files = Array.from(fileList);
    if (files.length === 0) return;
    if (!canAttach) {
      toast.error('You do not have permission to attach files');
      return;
    }
    for (const file of files) void handleAttach(file);
  };

  const handleGenerateImage = async () => {
    const prompt = draft.trim();
    if (!prompt) {
      toast.error('Describe the image in the composer first');
      return;
    }
    if (!bootstrap.data?.capabilities.imageGenerate) {
      toast.error('Image generation is not configured');
      return;
    }
    try {
      const threadId = await ensureThread();
      await mutations.generateImage.mutateAsync({ threadId, prompt });
      setDraft('');
      setImagePromptOpen(false);
      toast.success('Image generated');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Image generation failed');
    }
  };

  return (
    <HeliosChatWindow
      open={open}
      onOpenChange={setOpen}
      onReturnFocus={onReturnFocus}
      description="Claude business chat with OpenAI voice and image generation"
      badge={
        preferences?.opportunityAlerts !== false && pendingCount > 0 ? (
          <span className="border-warning/30 bg-warning/10 text-warning rounded-md border px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-[0.12em]">
            {pendingCount} pending
          </span>
        ) : undefined
      }
      onDragOver={(event) => {
        if (!canAttach) return;
        event.preventDefault();
        setDragging(true);
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={(event) => {
        event.preventDefault();
        setDragging(false);
        acceptDroppedFiles(event.dataTransfer.files);
      }}
    >
      {!canRead ? (
        <div className="p-4">
          <ErrorState
            title="Helios AI unavailable"
            description="Your role does not include ai:chat:read."
          />
        </div>
      ) : (
        <div className="grid min-h-0 flex-1 grid-cols-1 md:grid-cols-[9.75rem_minmax(0,1fr)]">
          <aside className="border-border/50 bg-card/30 hidden min-h-0 flex-col border-r md:flex">
            <div className="flex h-8 items-center justify-between px-2">
              <p className="text-muted-foreground text-[9px] font-semibold uppercase tracking-[0.18em]">
                Threads
              </p>
              <ComposerIconButton
                label="New conversation"
                disabled={!canWrite || mutations.createThread.isPending}
                onClick={() => {
                  void (async () => {
                    try {
                      approvalReview.clearTarget();
                      const thread = await mutations.createThread.mutateAsync(undefined);
                      setActiveThreadId(thread.id);
                    } catch (error) {
                      toast.error(
                        error instanceof Error ? error.message : 'Unable to create thread',
                      );
                    }
                  })();
                }}
              >
                <Plus className="h-3 w-3" />
              </ComposerIconButton>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto px-1.5 pb-2">
              {threads.isLoading ? (
                <Skeleton className="h-16 w-full" />
              ) : threads.isError ? (
                <p className="text-critical px-1.5 text-[11px]">Unable to load threads</p>
              ) : (threads.data?.length ?? 0) === 0 ? (
                <p className="text-muted-foreground px-1.5 text-[11px]">No conversations</p>
              ) : (
                <ul className="space-y-0.5">
                  {threads.data?.map((thread) => (
                    <li key={thread.id}>
                      <button
                        type="button"
                        className={[
                          'flex w-full items-start gap-1 rounded-md px-1.5 py-1.5 text-left text-[11px] leading-snug transition-colors',
                          thread.id === activeThreadId
                            ? 'border-primary/20 bg-primary/10 text-foreground shadow-glow border'
                            : 'text-muted-foreground hover:border-border/50 hover:bg-muted/40 hover:text-foreground border border-transparent',
                        ].join(' ')}
                        onClick={() => {
                          approvalReview.clearTarget();
                          setActiveThreadId(thread.id);
                        }}
                      >
                        {thread.pinned ? (
                          <Pin className="text-brand mt-0.5 h-2.5 w-2.5 shrink-0" />
                        ) : null}
                        <span className="line-clamp-2">{thread.title}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </aside>

          <section className="flex min-h-0 flex-col">
            {approvalReview.notice ? (
              <p role="status" className="px-3 py-2 text-[11px]">
                {approvalReview.notice}
              </p>
            ) : null}
            {approvalReview.items.length > 0 ? (
              <div className="border-border/50 bg-warning/[0.05] border-b px-3 py-2">
                <p className="text-warning mb-1.5 text-[9px] font-semibold uppercase tracking-[0.18em]">
                  Approvals
                </p>
                <ul className="space-y-1">
                  {approvalReview.items.map((item) => (
                    <li
                      key={`${item.kind}-${item.id}`}
                      aria-label={item.title}
                      tabIndex={item.id === approvalReview.focusId ? -1 : undefined}
                      ref={
                        item.id === approvalReview.focusId
                          ? approvalReview.focusSelected
                          : undefined
                      }
                      className="border-border/30 flex items-start justify-between gap-2 border-b py-1.5 last:border-0"
                    >
                      <div className="min-w-0">
                        <p className="truncate text-[12px] font-medium leading-tight">
                          {item.title}
                        </p>
                        <p className="text-muted-foreground mt-0.5 line-clamp-1 text-[11px]">
                          {item.summary}
                        </p>
                      </div>
                      <div className="flex shrink-0 gap-1">
                        {item.kind === 'tool_call' ? (
                          <>
                            {allowActionApproval ? (
                              <button
                                type="button"
                                className="text-primary hover:bg-primary/10 rounded-md px-1.5 py-0.5 text-[10px] font-medium disabled:opacity-40"
                                disabled={!canWrite || mutations.approveToolCall.isPending}
                                onClick={() => {
                                  void (async () => {
                                    try {
                                      await mutations.approveToolCall.mutateAsync(item.id);
                                      approvalReview.clearTarget();
                                      toast.success('Tool approved');
                                    } catch (error) {
                                      toast.error(
                                        error instanceof Error ? error.message : 'Approve failed',
                                      );
                                    }
                                  })();
                                }}
                              >
                                Approve
                              </button>
                            ) : (
                              <span className="text-muted-foreground text-[10px]">Draft only</span>
                            )}
                            <button
                              type="button"
                              className="text-muted-foreground hover:bg-muted/40 rounded-md px-1.5 py-0.5 text-[10px] font-medium disabled:opacity-40"
                              disabled={!canWrite || mutations.denyToolCall.isPending}
                              onClick={() => {
                                void (async () => {
                                  try {
                                    await mutations.denyToolCall.mutateAsync(item.id);
                                    approvalReview.clearTarget();
                                    toast.success('Tool denied');
                                  } catch (error) {
                                    toast.error(
                                      error instanceof Error ? error.message : 'Deny failed',
                                    );
                                  }
                                })();
                              }}
                            >
                              Deny
                            </button>
                          </>
                        ) : (
                          <>
                            {allowActionApproval ? (
                              <button
                                type="button"
                                className="text-primary hover:bg-primary/10 rounded-md px-1.5 py-0.5 text-[10px] font-medium disabled:opacity-40"
                                disabled={
                                  !canApproveRec || agentMutations.recommendationAction.isPending
                                }
                                onClick={() => {
                                  void (async () => {
                                    try {
                                      await agentMutations.recommendationAction.mutateAsync({
                                        id: item.id,
                                        action: 'APPROVE',
                                      });
                                      toast.success('Recommendation approved');
                                      await approvals.refetch();
                                      await bootstrap.refetch();
                                    } catch (error) {
                                      toast.error(
                                        error instanceof Error ? error.message : 'Approve failed',
                                      );
                                    }
                                  })();
                                }}
                              >
                                Approve
                              </button>
                            ) : (
                              <span className="text-muted-foreground text-[10px]">Draft only</span>
                            )}
                            <button
                              type="button"
                              className="text-muted-foreground hover:bg-muted/40 rounded-md px-1.5 py-0.5 text-[10px] font-medium disabled:opacity-40"
                              disabled={agentMutations.recommendationAction.isPending}
                              onClick={() => {
                                void (async () => {
                                  try {
                                    await agentMutations.recommendationAction.mutateAsync({
                                      id: item.id,
                                      action: 'DISMISS',
                                    });
                                    toast.success('Recommendation dismissed');
                                    await approvals.refetch();
                                    await bootstrap.refetch();
                                  } catch (error) {
                                    toast.error(
                                      error instanceof Error ? error.message : 'Dismiss failed',
                                    );
                                  }
                                })();
                              }}
                            >
                              Dismiss
                            </button>
                          </>
                        )}
                        {item.detailPath ? (
                          <button
                            type="button"
                            className="text-muted-foreground hover:bg-muted/40 rounded-md px-1.5 py-0.5 text-[10px] font-medium"
                            onClick={() => {
                              setOpen(false);
                              navigate(item.detailPath!);
                            }}
                          >
                            Details
                          </button>
                        ) : null}
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}

            <div ref={scrollerRef} className="min-h-0 flex-1 space-y-2 overflow-y-auto px-3 py-3">
              {bootstrap.isLoading || messages.isLoading ? (
                <Skeleton className="h-28 w-full" />
              ) : bootstrap.isError ? (
                <ErrorState
                  title="Unable to load Helios AI"
                  description={
                    bootstrap.error instanceof Error ? bootstrap.error.message : 'Unknown error'
                  }
                  action={
                    <Button size="sm" variant="outline" onClick={() => bootstrap.refetch()}>
                      Retry
                    </Button>
                  }
                />
              ) : !activeThreadId ? (
                <EmptyState
                  icon={<ConstructionOsLogo variant="toolbar" />}
                  title="Start a conversation"
                  description="Ask Claude about portfolio risk, schedule pressure, approvals, or integrations using the permitted platform snapshot."
                  action={
                    canWrite ? (
                      <Button
                        size="sm"
                        onClick={async () => {
                          approvalReview.clearTarget();
                          const thread = await mutations.createThread.mutateAsync(undefined);
                          setActiveThreadId(thread.id);
                        }}
                      >
                        New chat
                      </Button>
                    ) : undefined
                  }
                />
              ) : (messages.data?.items.length ?? 0) === 0 ? (
                <EmptyState
                  icon={<ConstructionOsLogo variant="toolbar" />}
                  title="Ready when you are"
                  description={
                    bootstrap.data?.capabilities.conversationalReply
                      ? 'Ask Claude a question. Attached files are saved with the conversation; chat does not read their contents.'
                      : 'Claude business chat is unavailable. Helios can show platform facts; OpenAI voice and images use separate settings.'
                  }
                />
              ) : (
                messages.data?.items.map((message) => (
                  <HeliosMessageBubble key={message.id} message={message} />
                ))
              )}
            </div>

            <div className="border-border/60 bg-card/50 backdrop-blur-glass border-t px-2.5 py-2">
              {preferencesQuery.isError ? (
                <p className="text-warning mb-1 text-[11px]">
                  AI preferences could not load. Spoken replies and action approvals are paused.
                </p>
              ) : null}
              {deviceVoice.listening ? (
                <p role="status" className="text-primary mb-1 text-[11px]">
                  Listening · {preferences?.voiceLanguage ?? 'en-US'} · Click the microphone to stop
                </p>
              ) : null}
              {voicePhase !== 'idle' ? (
                <p role="status" className="text-primary mb-1 text-[11px]">
                  {voicePhase === 'starting'
                    ? 'Waiting for microphone access'
                    : voicePhase === 'recording'
                      ? 'Recording for OpenAI transcription. Click the microphone to stop; auto-stops after 8 seconds.'
                      : 'Processing OpenAI voice input'}
                </p>
              ) : null}
              {deviceVoice.error ? (
                <p role="status" className="text-warning mb-1 text-[11px]">
                  {deviceVoice.error}
                </p>
              ) : null}
              {pendingAttachmentIds.length > 0 ? (
                <p className="text-muted-foreground mb-1 text-[10px]">
                  {pendingAttachmentIds.length} attachment
                  {pendingAttachmentIds.length === 1 ? '' : 's'} ready. Files are saved with the
                  conversation; chat does not read their contents.
                </p>
              ) : null}
              {imagePromptOpen ? (
                <p className="text-brand mb-1 text-[10px]">
                  OpenAI image mode - describe, then send to generate.
                </p>
              ) : null}
              <div className="flex items-end gap-1.5">
                <div className="mb-0.5 flex items-center gap-0.5">
                  <ComposerIconButton
                    label="Attach file"
                    title={
                      canAttach
                        ? 'Save a file with this conversation. Chat does not read its contents.'
                        : 'You do not have permission to attach files'
                    }
                    disabled={!canAttach}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <Paperclip className="h-3.5 w-3.5" />
                  </ComposerIconButton>
                  <ComposerIconButton
                    label={
                      voicePhase === 'recording'
                        ? 'Stop recording'
                        : deviceVoice.listening
                          ? 'Stop listening'
                          : capabilities?.voiceTranscribe
                            ? 'Record voice input'
                            : 'Start browser voice input'
                    }
                    title={
                      voiceDisabledReason ??
                      (voicePhase === 'recording'
                        ? 'Stop recording and transcribe with OpenAI'
                        : deviceVoice.listening
                          ? 'Stop listening'
                          : capabilities?.voiceTranscribe
                            ? preferences?.voiceAutoSend
                              ? 'OpenAI transcribes, Claude replies, then OpenAI speaks if spoken replies are enabled. Existing drafts are kept for review.'
                              : 'Record with OpenAI transcription, then review the text before sending to Claude'
                            : 'Browser-managed dictation fallback. OpenAI transcription is not configured.')
                    }
                    active={deviceVoice.listening || voicePhase === 'recording'}
                    disabled={
                      !canWrite ||
                      !voiceReady ||
                      voicePhase === 'starting' ||
                      voicePhase === 'processing'
                    }
                    onClick={() => void handleVoice()}
                  >
                    <Mic className="h-3.5 w-3.5" />
                  </ComposerIconButton>
                  <ComposerIconButton
                    label={canGenerateImage ? 'Toggle image generation' : 'Attach image'}
                    title={
                      imageDisabledReason ??
                      (canGenerateImage
                        ? imagePromptOpen
                          ? 'OpenAI image mode on - describe, then send to generate'
                          : 'Generate an image with OpenAI from the composer'
                        : 'Attach an image. OpenAI image generation is not configured; chat does not read attached images.')
                    }
                    active={imagePromptOpen}
                    disabled={!canWrite}
                    onClick={() => {
                      if (canGenerateImage) {
                        setImagePromptOpen((value) => !value);
                        return;
                      }
                      imageInputRef.current?.click();
                    }}
                  >
                    <ImagePlus className="h-3.5 w-3.5" />
                  </ComposerIconButton>
                  <ComposerIconButton
                    label="Speak last reply"
                    title={
                      speakDisabledReason ??
                      (hasSavedSpeech
                        ? 'Play the saved OpenAI speech for the last reply'
                        : canSpeakRemote
                          ? 'Read the last Claude reply with OpenAI speech'
                          : 'Read the last reply with browser-managed speech; OpenAI speech is not configured')
                    }
                    disabled={!speakReady}
                    onClick={() => {
                      void (async () => {
                        if (!lastAssistant) {
                          toast.error('No assistant message to speak');
                          return;
                        }
                        try {
                          await speakReply(lastAssistant, { manual: true });
                        } catch (error) {
                          toast.error(
                            error instanceof Error ? error.message : 'Unable to play speech',
                          );
                        }
                      })();
                    }}
                  >
                    <Volume2 className="h-3.5 w-3.5" />
                  </ComposerIconButton>
                </div>
                <HeliosChatComposerInput
                  value={draft}
                  onChange={setDraft}
                  onSend={() => {
                    if (imagePromptOpen) void handleGenerateImage();
                    else void handleSend();
                  }}
                  disabled={!canWrite}
                  sendDisabled={
                    mutations.sendMessage.isPending ||
                    mutations.generateImage.isPending ||
                    (!draft.trim() && pendingAttachmentIds.length === 0)
                  }
                  sendLabel={imagePromptOpen ? 'Generate image' : 'Send message'}
                  imageMode={imagePromptOpen}
                />
              </div>
              {dragging ? (
                <p className="text-primary mt-1 text-[10px]">Drop files to attach</p>
              ) : null}
              <input
                ref={fileInputRef}
                type="file"
                multiple
                data-testid="helios-file-input"
                className="hidden"
                onChange={(event) => {
                  const files = event.target.files;
                  event.target.value = '';
                  if (files) acceptDroppedFiles(files);
                }}
              />
              <input
                ref={imageInputRef}
                type="file"
                accept="image/*"
                multiple
                data-testid="helios-image-input"
                className="hidden"
                onChange={(event) => {
                  const files = event.target.files;
                  event.target.value = '';
                  if (files) acceptDroppedFiles(files);
                }}
              />
            </div>
          </section>
        </div>
      )}
    </HeliosChatWindow>
  );
}

/** @deprecated Prefer HeliosChatPanel — kept as alias for shell import stability. */
export function AiAdvisorDrawer() {
  return <HeliosChatPanel />;
}
